import ResizeObserver from "resize-observer-polyfill";

import {
  Component,
  ComponentFactory,
  ComponentRef,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ElementRef,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { jsPlumb } from "jsplumb";
import { BehaviorSubject, Subject } from "rxjs";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";
import {
  FlowNode,
  SideBarItem,
  FlowNodeController,
} from "../flow-node.controller";
import { FlowsService } from "../flows.service";
import { ConnectorOptionComponent } from "./connector-option/connector-option.component";
import { NotificationNodeComponent } from "./notification-node/notification-node.component";
import { EMailNodeComponent } from "./email-node/email-node.component";
import { PhoneNodeComponent } from "./phone-node/phone-node.component";
import { StarterNodeComponent } from "./starter-node/starter-node.component";
import { merge, reject } from "lodash";
import { LHPEPanzoom, LHPEPanzoomObject } from "src/app/shared/panzoom/panzoom";
import { first, takeUntil, takeWhile } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { FollowUpNodeComponent } from "./follow-up-node/follow-up-node.component";
import { I } from "@angular/cdk/keycodes";

const NODE_HEIGHT = 80;
const NODE_WIDTH = 80;

const NODE_HEIGHT_HALF = NODE_HEIGHT / 2;
const NODE_WIDTH_HALF = NODE_WIDTH / 2;

@Component({
  selector: "app-flow",
  templateUrl: "./flow.component.html",
  styleUrls: ["./flow.component.scss"],
})
export class FlowComponent implements OnInit {
  jsPlumbInstance = jsPlumb.getInstance({});
  @ViewChild("nodes", { read: ViewContainerRef })
  nodesViewContainerRef!: ViewContainerRef;
  nodes: FlowNode[] = [];
  @ViewChild("flowMenu", { read: ViewContainerRef })
  flowMenuViewContainerRef!: ViewContainerRef;

  sidebarItems!: SideBarItem[];
  currentCursor?: string;
  panzoom!: LHPEPanzoomObject;

  draggableSubject = new BehaviorSubject(true);
  mouseDown: boolean = false;
  mouseDownX!: number;
  mouseDownY!: number;
  newFlow!: boolean;
  facilities: any;
  integrations: any;
  connections: {
    source: string;
    target: string;
    data: any;
    id: string;
    name: string;
    component?: ConnectorOptionComponent;
  }[] = [];
  flow: any;
  startConnectionNodeController?: FlowNodeController | null;
  private _selectedNodeController?: FlowNodeController | null;
  private _selectedConnectorOptionComponent?: ConnectorOptionComponent | null;
  selectedFlowItemSubject = new BehaviorSubject<
    FlowNodeController | ConnectorOptionComponent | null | undefined
  >(null);
  mode: {
    inNodeConnectionMode: boolean;
    inNodeDragMode: boolean;
    inNodeAddMode: boolean;
  } = {
    inNodeConnectionMode: false,
    inNodeDragMode: false,
    inNodeAddMode: false,
  };
  onMenuOpenChange = new Subject<{
    type: string;
    menuOpenChange: boolean;
    element: ElementRef;
    menuElement: ElementRef;
  }>();
  onNodeOrConnectorMoved = new Subject<{
    type: string;
    component: FlowNodeController | ConnectorOptionComponent;
  }>();
  @ViewChild("menuShadow") menuShadowElement!: ElementRef;
  @ViewChild("flow") flowElement!: ElementRef;
  @ViewChild("panzoomArea") panzoomArea!: ElementRef;

  openMenuShadowObj?: {
    type: string;
    element: ElementRef<any>;
    menuElement: ElementRef<any>;
  };
  onMenuSizeChange = new Subject<any>();
  resizeObserver!: ResizeObserver;
  private onComponentDestroy = new Subject();
  flowName = "";
  internal = false;

  constructor(
    private ChangeDetectorRef: ChangeDetectorRef,
    private factoryResolver: ComponentFactoryResolver,
    private route: ActivatedRoute,
    private FlowsService: FlowsService,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private Router: Router,
    private uiService: UiService,
    private Dialog: MatDialog,
  ) {}

  async ngOnInit(): Promise<void> {

    //console.log('FLOW NG INIT')

    this.flow = this.route.snapshot.data.flow;
    this.internal = this.route.snapshot.params.type == 'internal';
    this.newFlow = !this.flow;
    
    this.initFacilities();

    this.initIntegrations();

    this.initSidebarItems();

    if (this.flow) {
      this.initFlow();
      this.flowName = this.flow.name;
    }
    
  }

  async ngAfterViewInit() {

    this.route.data.subscribe(data => {
      var isInternal = this.route.snapshot.params.type == 'internal';
      if(isInternal != this.internal){
        this.internal = isInternal;
      }
    })

    //If there is already a panzoom is currently initiated, wait for it to get destroyed before reinitializing another one
    if (this.FlowsService.panzoomInitiated.value) {
      await this.uiService
        .waitForEvent(
          this.FlowsService.panzoomInitiated.pipe(
            first((initiated) => !initiated),
          ),
        )
        .toPromise();
    }

    const panZoomArea = document.getElementById("panzoom-area");

    this.panzoom = LHPEPanzoom(panZoomArea!, {
      handleStartEvent: (event: Event) => {},
      canvas: true,
      disablePan: true,
    });

    this.uiService
      .waitForEvent(this.panzoom.onInit)
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(async () => {

        //console.log('PANZOOM READY')

        this.FlowsService.panzoomInitiated.next(true);

        //If new flow, add starter node
        if (this.newFlow) {
          const panZoomArea = document.getElementById("panzoom-area");
          const panzoomBoundRect = panZoomArea!.getBoundingClientRect();
          const starterNode = await this.createStarterNode(
            panzoomBoundRect.width / 2 - 40,
            panzoomBoundRect.height / 8 - 40,
          );
          this.nodes.push(starterNode);

          //Select start node of new flow
          //TODO: Wait for shadow to be fully init to call this components init to avoid having to do hacky workarounds like this
          setTimeout(() => {
            starterNode.component?.toggleNode();
          }, 300);
        } else {
          for (const node of this.nodes) {
            if (node.type === "starter") {
              this.initStarterNodeData(node);
              await this.addStarterNode(node);

            } else if (node.type === "phone") {
              this.initPhoneNodeData(node);
              await this.addPhoneNode(node);

            } else if (node.type === "notification") {
              await this.addNotificationNode(node);

            } else if (node.type === "email") {
              await this.addEmailNode(node);
            }
          }
        }

        this.ChangeDetectorRef.detectChanges();

        this.draggableSubject
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe((value) => {
            this.setDraggable(value);
          });

        setTimeout(() => {
          this.connections.forEach((connection) => {
            connection.data.trigger = this.trigger
            this.addConnection(connection);
          });

          this.ChangeDetectorRef.detectChanges();
        });

        this.setPanZoomScale();

        this.ChangeDetectorRef.detectChanges();

        this.onMenuOpenChange
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe((menuOpenChangeObj) => {
            const { type, menuOpenChange, element, menuElement } =
              menuOpenChangeObj;
            if (menuOpenChange) {
              setTimeout(() => {
                this.calculateMenuShadow(type, element, menuElement);
                this.menuShadowElement.nativeElement.hidden = false;
                this.openMenuShadowObj = { type, element, menuElement };
              });
            } else {
              this.openMenuShadowObj = undefined;
              this.menuShadowElement.nativeElement.hidden = true;
            }
          });

        this.onNodeOrConnectorMoved
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe((moveObject) => {
            const { type, component } = moveObject;
            if (component.menuComponent!.instance.isOpen) {
              if (type === "node") {
                this.calculateMenuShadow(
                  type,
                  (component as FlowNodeController).nodeElement,
                  (component as FlowNodeController).menuComponent!.instance
                    .nodeMenuElement,
                );
              } else if (type === "connector") {
                this.calculateMenuShadow(
                  type,
                  (component as ConnectorOptionComponent).element,
                  (component as ConnectorOptionComponent).menuComponent!
                    .instance.connectorMenuElement,
                );
              }
            }
          });

        this.onMenuSizeChange
          .pipe(takeUntil(this.onComponentDestroy))
          .subscribe(() => {
            if (this.openMenuShadowObj) {
              const { type, element, menuElement } = this.openMenuShadowObj;

              this.calculateMenuShadow(type, element, menuElement);
            }
          });

        this.resizeObserver = new ResizeObserver((entries) => {
          //If shadow is showing, recalcute on flow area resize
          if (this.openMenuShadowObj) {
            const { type, element, menuElement } = this.openMenuShadowObj;
            this.calculateMenuShadow(type, element, menuElement);
          }
        });

        this.resizeObserver.observe(this.flowElement.nativeElement);
      });
  }

  ngOnDestroy() {
    this.FlowsService.invalidateFlowCache();

    this.panzoom.destroy();
    this.onNodeOrConnectorMoved.unsubscribe();
    this.onMenuOpenChange.unsubscribe();
    this.onMenuSizeChange.unsubscribe();
    this.draggableSubject.unsubscribe();

    this.panzoom.onDestroy
      .pipe(takeWhile(() => this.FlowsService.panzoomInitiated.value))
      .subscribe(() => {
        this.FlowsService.panzoomInitiated.next(false);
      });

    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get selectedSidebarItem() {
    return this.sidebarItems?.find((item) => item.selected);
  }

  get hasSelectedSidebarItem() {
    return !!this.selectedSidebarItem;
  }

  get hasStarterNodeInFlow() {
    return this.nodes.find((node) => {
      return node.type === "starter";
    });
  }

  get sidebarDisabled() {
    return this.mode.inNodeConnectionMode;
  }

  get nodeSelected() {
    return !!this.selectedNodeController;
  }

  get connectorOptionsSelected() {
    return !!this.selectedConnectorOptionComponent;
  }

  initFlow() {
    //console.log('FLOW INIT')
    for (let node of this.flow.logic.nodes) {
      if (node.type === "start") {
        this.nodes.push({
          type: "starter",
          top: node.top ?? 75,
          left: node.left ?? 399,
          id: node.id,
          data: node.data,
        });
      } else if (node.type === "signal") {
        if (node.data.type === "push") {
          this.nodes.push({
            type: "phone",
            top: node.top ?? 368,
            left: node.left ?? 399,
            id: node.id,
            is_follow_up: node.is_follow_up,
            data: node.data,
          });
        }
      } else if (node.type === "notification") {
        this.nodes.push({
          type: "notification",
          top: node.top ?? 674,
          left: node.left ?? 399,
          id: node.id,
          data: node.data,
        });
      } else if (node.type === "email") {
        this.nodes.push({
          type: "email",
          top: node.top ?? 674,
          left: node.left ?? 399,
          id: node.id,
          data: node.data,
          is_follow_up: node.is_follow_up
        });
      }
    }

    //TODO REVISIT THIS
    //Only add edges that we have created a node for
    if (this.flow.logic.v3_flow) {
      this.connections = this.flow.logic.edges.filter((edge: any) => {
        return (
          !!this.nodes.find((node) => {
            return edge.source === node.id;
          }) &&
          !!this.nodes.find((node) => {
            return edge.target === node.id;
          })
        );
      });
    } else {
      //v2 edges change target and source ids on update which might not align with the ids on the nodes

      //In v2 we'll only have 1 signal condition connection
      const signalConnection = this.flow.logic.edges.find((edge: any) => {
        return edge.name === "Signal Conditions";
      });

      const notificationConnection = this.flow.logic.edges.find((edge: any) => {
        return edge.name === "Notification Conditions";
      });

      if (signalConnection) {
        //find the starter and end node ids and asign it as the source and target
        signalConnection.source = this.nodes.find(
          (node: any) => node.type === "starter",
        )!.id;
        signalConnection.target = this.nodes.find(
          (node: any) => node.type === "phone",
        )!.id;

        this.connections.push(signalConnection);
      }

      if (notificationConnection) {
        notificationConnection.source = this.nodes.find(
          (node: any) => node.type === "phone",
        )!.id;

        notificationConnection.target = this.nodes.find(
          (node: any) => node.type === "notification",
        )!.id;

        this.connections.push(notificationConnection);
      }
    }

    this.connections.forEach((connection) => {
      var src = this.nodes.find(
        (node) => node.id === connection.source,
      )
      var tgt = this.nodes.find(
        (node) => node.id === connection.target,
      )
      connection.data.sourceType = src!.type;
      connection.data.targetType = tgt!.type;
      connection.data.trigger = this.trigger;
      connection.data.id = connection.id;
    });
  }

  initFacilities() {
    this.facilities = this.route.snapshot.data.facilities.records.map(
      (facility: any) => {
        return {
          ...facility,
          selected: true,
        };
      },
    );
  }

  initIntegrations() {
    this.integrations = this.route.snapshot.data.integrations;
  }

  initStarterNodeData(node: FlowNode) {
    node.data.flowData = {
      name: this.flow.name,
      facilities: this.flow.facilities,
      startDate: this.flow.start_date,
      endDate: this.flow.end_date,
      entryEventObjectIDs: this.flow.entry_event_object_ids,
      entryEventTypeID: this.flow.entry_event_type_id,
      entryEventInterval: this.flow.entry_event_interval,
      apiID: this.flow.api_id,
      apiAuth: this.flow.api_auth,
    };
  }

  initPhoneNodeData(node: FlowNode) {
    node.data.flowData = {
      conversionBeaconRegions: this.flow.conversion_beacon_regions,
      conversionGeofences: this.flow.conversion_geofences,
      conversionLength: this.flow.conversion_length,
      payload: this.flow.payload,
      accountAppOfferData: this.flow.account_app_offer_data,
      accountAppOfferID: this.flow.account_app_offer_id,
    };
  }

  calculateMenuShadow(
    type: string,
    element: ElementRef,
    menuElement: ElementRef,
  ) {
    const flowElementRect =
      this.flowElement.nativeElement.getBoundingClientRect();
    const elementRect = element.nativeElement.getBoundingClientRect();
    const menuElementRect = menuElement.nativeElement.getBoundingClientRect();

    this.menuShadowElement.nativeElement.style.top = "0px";
    this.menuShadowElement.nativeElement.style.left = `${
      elementRect.right - flowElementRect.left
    }px`;

    this.menuShadowElement.nativeElement.style.width = `${
      menuElementRect.left - elementRect.right
    }px`;
    this.menuShadowElement.nativeElement.style.height = `${
      Math.max(elementRect.bottom, menuElementRect.bottom) -
      Math.min(menuElementRect.top, elementRect.top)
    }px`;

    let point1: number[] = [];
    let point2: number[] = [];
    if (type === "node") {
      point1 = [0, elementRect.bottom - flowElementRect.top];
      point2 = [0, elementRect.top - flowElementRect.top];
    }
    if (type === "connector") {
      point1 = [
        0,
        (elementRect.bottom -
          flowElementRect.top +
          (elementRect.top - flowElementRect.top)) /
          2,
      ];
      point2 = [
        0,
        (elementRect.bottom -
          flowElementRect.top +
          (elementRect.top - flowElementRect.top)) /
          2,
      ];
    }
    const point3 = [menuElementRect.left - elementRect.right, 14];
    const point4 = [
      menuElementRect.left - elementRect.right,
      menuElementRect.height - 14,
    ];

    this.menuShadowElement.nativeElement.firstElementChild.firstElementChild.setAttribute(
      "points",
      [...point1, ...point2, ...point3, ...point4],
    );
  }

  setPanZoomScale() {
    //Calculate zoom level needed to show all nodes
    const flowBuilderAreaRect = document
      .getElementById("flow-builder-area")!
      .getBoundingClientRect();

    const nodeHeight = 80;
    const padding = 20;

    let minX: number = 0;
    let minY: number = 0;
    let maxX: number = flowBuilderAreaRect.width;
    let maxY: number = flowBuilderAreaRect.height;

    this.nodes.forEach((node) => {
      if (node.left! < minX) {
        minX = node.left!;
      }
      if (node.left! > maxX) {
        maxX = node.left!;
      }
      if (node.top! < minY) {
        minY = node.top!;
      }
      if (node.top! > maxY) {
        maxY = node.top!;
      }
    });

    const minXScale =
      minX !== 0
        ? flowBuilderAreaRect.width /
          (2 * (Math.abs(minX) + padding) + flowBuilderAreaRect.width)
        : 1;

    const maxXScale =
      maxX !== flowBuilderAreaRect.width
        ? flowBuilderAreaRect.width /
          (2 * (maxX - flowBuilderAreaRect.width + nodeHeight + padding) +
            flowBuilderAreaRect.width)
        : 1;

    const minYScale =
      minY !== 0
        ? flowBuilderAreaRect.height /
          (2 * (Math.abs(minY) + padding) + flowBuilderAreaRect.height)
        : 1;

    const maxYScale =
      maxY !== flowBuilderAreaRect.height
        ? flowBuilderAreaRect.height /
          (2 * (maxY - flowBuilderAreaRect.height + nodeHeight + padding) +
            flowBuilderAreaRect.height)
        : 1;

    const scale = Math.min(minXScale, maxXScale, minYScale, maxYScale);

    this.panzoom.zoom(scale);
  }

  getXYCoordinatesRelativeToPanzoomArea($event: MouseEvent) {
    //@ts-ignore
    if ($event.target!.id === "panzoom-area") {
      const x = $event.offsetX;
      const y = $event.offsetY;

      return { x, y };
    } else {
      //if flow builder area was click, get the offset relative to the panzoom area

      const panzoomAreaElem = document.getElementById("panzoom-area")!;
      const flowBuilderAreaElem = document.getElementById("flow-builder-area")!;

      const panZoomAreaRect = panzoomAreaElem.getBoundingClientRect();

      const flowAreaRect = flowBuilderAreaElem.getBoundingClientRect();

      const topDiff = panZoomAreaRect.top - flowAreaRect.top;

      const leftDiff = panZoomAreaRect.left - flowAreaRect.left;

      const scale = this.panzoom.getScale();

      const x = ($event.offsetX - leftDiff) / scale;

      const y = ($event.offsetY - topDiff) / scale;

      return { x, y };
    }
  }

  initSidebarItems() {
    this.sidebarItems = [
      {
        code: "phone",
        name: "Push",
        icon: "assets/svg/flows/sidebar/phone.svg",
        disabled: false,
        disabledFn: () => {
          return this.internal || !this.hasTrigger();
        },
        selected: false,
        channel: true,
        onFlowAreaClick: (
          object: any,
          isFollowUp = false,
          openOnInit = false,
        ) => {
          const { $event, currentItem } = object;

          let x, y;

          if (isFollowUp) {
            x = object.x;
            y = object.y;
          } else {
            const xy = this.getXYCoordinatesRelativeToPanzoomArea($event);
            x = xy.x;
            y = xy.y;
          }

          const node = this.createPhoneNode(
            x - NODE_WIDTH_HALF,
            y - NODE_HEIGHT_HALF,
            isFollowUp,
            openOnInit,
          );

          this.nodes.push(node);

          currentItem.selected = false;

          this.resetCursor();
          this.setDraggable(true);
          this.mode.inNodeAddMode = false;
        },
        onSidebarClick: (object: any) => {
          this.currentCursor = "crosshair";
          this.setDraggable(false);
          this.mode.inNodeAddMode = true;
        },
      },
      {
        code: "notification",
        name: "Notification",
        icon: "assets/svg/flows/sidebar/notification.svg",
        disabled: false,
        disabledFn: () => {
          return this.internal || !this.hasTrigger();
        },
        selected: false,
        channel: false,
        onFlowAreaClick: (object: any, isFollowUp = false) => {
          const { $event, currentItem } = object;

          const { x, y } = this.getXYCoordinatesRelativeToPanzoomArea($event);

          const node = this.createNotificationNode(
            x - NODE_WIDTH_HALF,
            y - NODE_HEIGHT_HALF,
          );

          this.nodes.push(node);

          currentItem.selected = false;

          this.resetCursor();
          this.setDraggable(true);
          this.mode.inNodeAddMode = false;
        },
        onSidebarClick: (object: any) => {
          this.currentCursor = "crosshair";
          this.setDraggable(false);
          this.mode.inNodeAddMode = true;
        },
      },
      {
        code: "email",
        name: "Email",
        icon: "assets/svg/flows/sidebar/email.svg",
        disabled: false,
        disabledFn: () => {
          return this.integrations.filter((i: any) => {
            return i.provider.type == 'email';
          }) == 0 || !this.hasTrigger();
        },
        channel: true,
        selected: false,
        onFlowAreaClick: (
          object: any,
          isFollowUp = false,
          openOnInit = false,
        ) => {
          const { $event, currentItem } = object;

          let ex, ey;

          if (isFollowUp) {
            ex = object.x;
            ey = object.y;
          } else {
            const xy = this.getXYCoordinatesRelativeToPanzoomArea($event);
            ex = xy.x;
            ey = xy.y;
          }

          const enode = this.createEmailNode(
            ex - NODE_WIDTH_HALF,
            ey - NODE_HEIGHT_HALF,
            isFollowUp,
            openOnInit,
          );

          this.nodes.push(enode);

          currentItem.selected = false;

          this.resetCursor();
          this.setDraggable(true);
          this.mode.inNodeAddMode = false;

        },
        onSidebarClick: (object: any) => {
          this.currentCursor = "crosshair";
          this.setDraggable(false);
          this.mode.inNodeAddMode = true;
        },
      },
      {
        code: "followUp",
        name: "Follow Up",
        icon: "assets/svg/flows/sidebar/followUp.svg",
        disabled: false,
        disabledFn: () => {
          return !this.hasChannelNode || !this.hasTrigger();
        },
        selected: false,
        channel: false,
        onFlowAreaClick: (object: any) => {
          // this.Dialog.open(FollowUpNodeDialogComponent, {
          //   data: {
          //     sidebarItems: this.sidebarItems,
          //   },
          // })
          //   .afterClosed()
          //   .pipe(takeUntil(this.onComponentDestroy))
          //   .subscribe((res) => {
          //     if (res) {
          //       res.onFlowAreaClick(object, true);
          //     }
          //   });

          const { $event, currentItem } = object;

          const { x, y } = this.getXYCoordinatesRelativeToPanzoomArea($event);

          const node = this.createFollowUpNode(
            x - NODE_WIDTH_HALF,
            y - NODE_HEIGHT_HALF,
          );

          this.nodes.push(node);

          currentItem.selected = false;

          this.resetCursor();
          this.setDraggable(true);
          this.mode.inNodeAddMode = false;
        },
        onSidebarClick: (object: any) => {
          this.currentCursor = "crosshair";
          this.setDraggable(false);
          this.mode.inNodeAddMode = true;
        },
      },
    ];

    this.ChangeDetectorRef.detectChanges();
  }

  setDraggable(value: boolean) {
    setTimeout(() => {
      //this.mode.inNodeDragMode = value; // not sure why this would have been necessary
      for (let node of this.nodes) {
        this.jsPlumbInstance.setDraggable(node.id, value);
      }
    });
  }

  addDynamicNode(factory: ComponentFactory<any>, node: FlowNode, internal: boolean = false, facilities: any) {
    return new Promise((resolve, reject) => {
      //console.log('ADD DYNAMIC NODE START', factory.selector)
      const component: ComponentRef<FlowNodeController> = factory.create(
        this.nodesViewContainerRef.parentInjector,
      );
      component.instance.node = node;
      component.instance.jsPlumbInstance = this.jsPlumbInstance;
      component.instance.nodes = this.nodes;
      component.instance.connections = this.connections;
      component.instance.componentRef = component;
      component.instance.panzoom = this.panzoom;
      component.instance.mode = this.mode;
      component.instance.flowMenuViewContainerRef = this.flowMenuViewContainerRef;
      component.instance.onMenuOpenChange = this.onMenuOpenChange;
      component.instance.onNodeOrConnectorMoved = this.onNodeOrConnectorMoved;
      component.instance.onMenuSizeChange = this.onMenuSizeChange;
      component.instance.selectedFlowItemSubject = this.selectedFlowItemSubject;
      component.instance.internal = internal;
      component.instance.facilities = facilities;

      //console.log(factory.selector);

      // Follow-Up Node Needs Channels
      if(factory.selector === 'app-follow-up-node'){
        (component.instance as FollowUpNodeComponent).channelItems = this.sidebarItems.filter((item) => {
          let include = item.channel && !(item.disabledFn ? item.disabledFn() : true);
          return include;
        });
      }
  
      component.instance.onNodeReady.pipe(takeUntil(this.onComponentDestroy))
      .subscribe((nodeComponentRef: ComponentRef<FlowNodeController>) => {
        //console.log('ON NODE READY', nodeComponentRef)
        resolve(component)
      });
  
      component.instance.onNodeDelete
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((nodeComponentRef: ComponentRef<FlowNodeController>) => {
          this.onNodeDelete(nodeComponentRef);
        });
  
      component.instance.onDeleteConnector
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((data: any) => {
          this.deleteConnection(data.connectorData);
          if (data.connectorOptionComponent) {
            data.connectorOptionComponent.destroy();
          }
        });
  
      component.instance.onStartNodeConnection
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((nodeController: FlowNodeController) => {
          this.startConnectionNodeController = nodeController;
          this.mode.inNodeConnectionMode = true;
        });
  
      component.instance.onNodeSelected.subscribe(
        (nodeController: FlowNodeController) => {
          this.setSelectedNodeController(nodeController);
        },
      );
  
      component.instance.onCompleteNodeConnection
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((completeNodeController: FlowNodeController) => {

          //console.log('ON COMPLETE NODE CONNECTION')
  
          const connections = this.jsPlumbInstance.getAllConnections();
          //source target not working so filtering it out manually
          const connectionsFromBoth = connections.filter((connection) => {
            return (
              (connection.sourceId ===
                this.startConnectionNodeController!.node.id &&
                connection.targetId === completeNodeController!.node.id) ||
              (connection.sourceId === completeNodeController!.node.id &&
                connection.targetId ===
                  this.startConnectionNodeController!.node.id)
            );
          });
  
          //if no connection exists create one
          if (!connectionsFromBoth.length) {
            const sourceNode = this.nodes.find(
              (node) => node.id === this.startConnectionNodeController!.node.id,
            )!;
            const targetNode = this.nodes.find(
              (node) => node.id === completeNodeController!.node.id,
            )!;
  
            let connectionName = "";
            if (targetNode.type === "phone") {
              connectionName = "Signal Conditions";
            } else if (targetNode.type === "notification") {
              connectionName = "Notification Conditions";
            } else if (targetNode.type === "email") {
              connectionName = "Email Conditions";
            }
  
            const id = `con_${Math.random().toString(16).slice(2, 8)}`;
  
            const connection = {
              id,
              name: connectionName,
              data: {
                conditions: [],
                condition_mode: "structured",
                sourceType: sourceNode.type,
                targetType: targetNode.type,
                is_follow_up: targetNode.is_follow_up,
                trigger: this.trigger,
                id,
              },
              source: sourceNode.id,
              target: targetNode.id,
            };
  
            this.connections.push(connection);
            this.addConnection(connection);
          }
          this.startConnectionNodeController!.linkOptionSelected = false;
          this.startConnectionNodeController = null;
          this.mode.inNodeConnectionMode = false;
  
          if (this.selectedNodeController) {
            this.setSelectedNodeController(null);
          }
        });
  
      component.instance.onConnectorOptionsSelectChange
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe((connectOptionComponent: ConnectorOptionComponent | null) => {
          this.setSelectedConnectorOptionComponent(connectOptionComponent);
        });
  
      this.nodesViewContainerRef.insert(component.hostView);
  
      node.component = component.instance;

      //console.log('ADD DYNAMIC NODE END')

    })
  }

  async createStarterNode(x: number, y: number) {
    //console.log('CREATE STARTER NODE')
    const node: FlowNode = {
      id: "node_" + [Math.random().toString(16).slice(2, 8)],
      left: x,
      top: y,
      type: "starter",
      data: {},
      component: undefined,
    };

    await this.addStarterNode(node);

    return node;
  }

  createPhoneNode(
    x: number,
    y: number,
    isFollowUp: boolean = false,
    openOnInit: boolean = false,
  ) {
    const node = {
      id: "node_" + [Math.random().toString(16).slice(2, 8)],
      left: x,
      top: y,
      type: "phone",
      is_follow_up: isFollowUp,
      data: {},
    };

    this.addPhoneNode(node, openOnInit);

    return node;
  }

  createNotificationNode(x: number, y: number) {
    const node = {
      id: "node_" + [Math.random().toString(16).slice(2, 8)],
      left: x,
      top: y,
      type: "notification",
      data: {},
    };

    this.addNotificationNode(node);

    return node;
  }

  createFollowUpNode(x: number, y: number) {
    const node = {
      id: "node_" + [Math.random().toString(16).slice(2, 8)],
      left: x,
      top: y,
      type: "followUp",
      is_follow_up: true,
      data: {},
    };

    this.addFollowUpNode(node);

    return node;
  }

  createEmailNode(x: number, y: number, isFollowUp: boolean = false,
    openOnInit: boolean = false) {
    const node = {
      id: "node_" + [Math.random().toString(16).slice(2, 8)],
      left: x,
      top: y,
      type: "email",
      is_follow_up: isFollowUp,
      data: {},
    };

    this.addEmailNode(node, openOnInit);

    return node;
  }

  addStarterNode(node: FlowNode) {
    //console.log('ADD STARTER NODE')

    return new Promise(async (resolve, reject) => {
      const factory = this.factoryResolver.resolveComponentFactory(StarterNodeComponent);
      const component: ComponentRef<StarterNodeComponent> = await this.addDynamicNode(
        factory,
        node,
        this.internal,
        this.facilities
      ) as ComponentRef<StarterNodeComponent>;

      //Once starter node is added enable all other nodes
      for (let item of this.sidebarItems) {
        if (item.code !== "starter") {
          item.disabled = false;
        } else {
          item.disabled = true;
        }
      }
      resolve(component.instance)
    })

  }

  addPhoneNode(node: FlowNode, openOnInit: boolean = false) {
    //console.log('ADD PHONE NODE')

    return new Promise(async (resolve, reject) => {

      const factory = this.factoryResolver.resolveComponentFactory(PhoneNodeComponent);

      node.channelNode = true;

      const component: ComponentRef<PhoneNodeComponent> = await this.addDynamicNode(
        factory,
        node,
        this.internal,
        this.facilities
      ) as ComponentRef<PhoneNodeComponent>;

      if (openOnInit) {
        setTimeout(() => {
          this.setSelectedNodeController(
            component.instance as FlowNodeController,
          );
        });
      }

      resolve(component.instance)

    })
  }

  addNotificationNode(node: FlowNode) {
    //console.log('ADD NOTIFICATION NODE')
    return new Promise(async (resolve, reject) => {
      const factory = this.factoryResolver.resolveComponentFactory(
        NotificationNodeComponent,
      );
      const component: ComponentRef<NotificationNodeComponent> =
        await this.addDynamicNode(
          factory,
          node,
          this.internal,
          this.facilities,
        ) as ComponentRef<NotificationNodeComponent>;

      resolve(component.instance)

    })
  }

  async addFollowUpNode(node: FlowNode) {
    return new Promise(async (resolve, reject) => {
      const factory = this.factoryResolver.resolveComponentFactory(
        FollowUpNodeComponent,
      );

      const component: ComponentRef<FollowUpNodeComponent> = await this.addDynamicNode(
        factory,
        node,
        this.internal,
        this.facilities,
      ) as ComponentRef<FollowUpNodeComponent>;

      resolve(component.instance)
    })
  }

  async addEmailNode(node: FlowNode, openOnInit: boolean = false) {
    //console.log('ADD EMAIL NODE')

    return new Promise(async (resolve, reject) => {

      const factory = this.factoryResolver.resolveComponentFactory(
        EMailNodeComponent,
      );

      node.channelNode = true;

      const component: ComponentRef<EMailNodeComponent> = await this.addDynamicNode(
        factory,
        node,
        this.internal,
        this.facilities,
      ) as ComponentRef<EMailNodeComponent>;

      if (openOnInit) {
        setTimeout(() => {
          this.setSelectedNodeController(
            component.instance as FlowNodeController,
          );
        });
      }

      resolve(component.instance)

    })
  }

  onSidebarItemClick(item: any) {
    if (
      item.disabledFn
        ? item.disabledFn()
        : item.disabled || this.sidebarDisabled
    ) {
      return;
    }

    const selectedItems = this.sidebarItems.filter((item) => item.selected);

    if (!item.selected) {
      for (let selectedItem of selectedItems) {
        selectedItem.selected = false;
      }

      item.selected = true;
      if (item.onSidebarClick) {
        item.onSidebarClick();
      }
    }
  }

  resetCursor() {
    this.currentCursor = "default";
  }

  onFlowBuilderAreaClicked($event: MouseEvent) {
    const wasPanning = !this.panzoom.getOptions().disablePan;
    //If we were just panning, click triggers after mouseup so disablePan
    if (wasPanning) {
      this.panzoom.setOptions({ disablePan: true });
    }

    if (this.hasSelectedSidebarItem) {
      if (this.selectedSidebarItem!.onFlowAreaClick) {
        this.selectedSidebarItem!.onFlowAreaClick({
          $event,
          currentItem: this.selectedSidebarItem,
        });
      }
    } else {
      //If node or option is selected and we werent panning, close its menu
      if (this.selectedFlowItemSubject.value && !wasPanning) {
        this.selectedFlowItemSubject.value.setSelected(false);
      }
    }
  }

  onMouseDown($event: MouseEvent) {
    //Check that its not right click
    if ($event.button !== 2) {
      //Hold values for panning
      this.mouseDown = true;
      this.mouseDownX = $event.x;
      this.mouseDownY = $event.y;
    }
  }

  onMouseUpOrLeave($event: MouseEvent) {
    this.mouseDown = false;
  }

  onMouseMove($event: MouseEvent) {
    //If mouse is still down pan to the difference
    if (this.mode.inNodeDragMode && this.mouseDown) {
      if (this.panzoom.getOptions().disablePan) {
        this.panzoom.setOptions({ disablePan: false });
      }

      //If we're panning and menu is open calculate the shadow
      if (this.openMenuShadowObj) {
        const { type, element, menuElement } = this.openMenuShadowObj;
        this.calculateMenuShadow(type, element, menuElement);
      }
    }
  }

  cancelNodeAdd() {
    this.resetCursor();
    this.setDraggable(true);
    this.mode.inNodeAddMode = false;

    this.sidebarItems
      .filter((item) => item.selected)
      .forEach((item) => (item.selected = false));
  }

  cancelConnection() {
    if (this.startConnectionNodeController) {
      this.startConnectionNodeController.linkOptionSelected = false;
      this.startConnectionNodeController = null;
      this.mode.inNodeConnectionMode = false;
    }
  }

  deleteConnection(connection: any) {
    const con = this.connections.find((con) => con.id === connection.id)!;
    const conIndex = this.connections.indexOf(con);

    this.connections.splice(conIndex, 1);

    const jsPlumConnection = this.jsPlumbInstance
      .getAllConnections()
      .find((jsPlumbConnect) => jsPlumbConnect.id === connection.id)!;

    this.jsPlumbInstance.deleteConnection(jsPlumConnection);
  }

  addConnection(connection: any) {
    //console.log('ADD CONNECTION')
    const source = this.jsPlumbInstance.getEndpoint(
      connection.source + "_bottom",
    );
    const target = this.jsPlumbInstance.getEndpoint(connection.target + "_top");

    const jsPlumbConnection = this.jsPlumbInstance.connect({
      source,
      target,
      parameters: connection.data,
    });

    //change id to connection id
    //@ts-ignore
    jsPlumbConnection.id = connection.id;
  }

  onNodeDelete(componentRef: ComponentRef<FlowNodeController>) {
    componentRef.destroy();
  }

  zoomIn() {
    this.panzoom.zoomIn();
    setTimeout(() => {
      if (this.openMenuShadowObj) {
        const { type, element, menuElement } = this.openMenuShadowObj;
        this.calculateMenuShadow(type, element, menuElement);
      }
    }, 200);
  }

  zoomOut() {
    this.panzoom.zoomOut();

    setTimeout(() => {
      if (this.openMenuShadowObj) {
        const { type, element, menuElement } = this.openMenuShadowObj;
        this.calculateMenuShadow(type, element, menuElement);
      }
    }, 200);
  }

  center() {
    //If we have a node  or connector selected center it based on that
    if (this.nodeSelected || this.connectorOptionsSelected) {
      const panZoomArea = document.getElementById("panzoom-area");
      if (panZoomArea) {
        if (this.nodeSelected) {
          const nodeXCenter =
            panZoomArea.clientWidth / 2 -
            this.selectedNodeController!.node.left! -
            80;

          const nodeYCenter =
            panZoomArea.clientHeight / 2 -
            this.selectedNodeController!.node.top! -
            80;

          this.panzoom.pan(nodeXCenter, nodeYCenter, { force: true });
        } else if (this.connectorOptionsSelected) {
          const connectorBoundingRect =
            this.selectedConnectorOptionComponent!.element.nativeElement.getBoundingClientRect();

          const connectorXCenter =
            panZoomArea.clientWidth / 2 - connectorBoundingRect.left;

          const connectorYCenter =
            panZoomArea.clientHeight / 2 - connectorBoundingRect.top;

          this.panzoom.pan(connectorXCenter, connectorYCenter, { force: true });
        }
      }
    } else {
      this.panzoom.pan(0, 0, { force: true });
    }

    setTimeout(() => {
      if (this.openMenuShadowObj) {
        const { type, element, menuElement } = this.openMenuShadowObj;
        this.calculateMenuShadow(type, element, menuElement);
      }
    }, 200);
  }

  get selectedNodeController() {
    return this._selectedNodeController;
  }

  get selectedConnectorOptionComponent() {
    return this._selectedConnectorOptionComponent;
  }

  setSelectedNodeController(nodeController: FlowNodeController | null) {
    if (this.selectedFlowItemSubject.value) {
      this.selectedFlowItemSubject.value.setSelected(false);
      this.selectedFlowItemSubject.next(null);
    }
    if (nodeController) {
      this._selectedNodeController = nodeController;
      this.selectedFlowItemSubject.next(this.selectedNodeController);
      this.selectedFlowItemSubject.value!.setSelected(true);
    }
  }

  setSelectedConnectorOptionComponent(
    connectorOptionComponent: ConnectorOptionComponent | null,
  ) {
    if (this.selectedFlowItemSubject.value) {
      this.selectedFlowItemSubject.value.setSelected(false);
      this.selectedFlowItemSubject.next(null);
    }
    if (connectorOptionComponent) {
      this._selectedConnectorOptionComponent = connectorOptionComponent;
      this.selectedFlowItemSubject.next(this.selectedConnectorOptionComponent);
      this.selectedFlowItemSubject.value!.setSelected(true);
    }
  }

  saveFlow() {
    const logic: any = { nodes: [], edges: [], v3_flow: true };

    for (let node of this.nodes) {
      if (
        node.type === "starter" ||
        node.type === "phone" ||
        node.type === "notification" ||
        node.type === "email"
      ) {
        logic.nodes.push(node.component?.menuComponent?.instance.nodeDTO);
      }
    }

    for (let connection of this.connections) {
      const edgeData =
        connection.component?.edgeDataDTO ?? {
          conditions: connection.component?.conditionsDTO ?? [],
          is_follow_up: connection.data.is_follow_up,
          condition_mode: connection.data.condition_mode ?? "structured",
        };
      logic.edges.push({
        id: connection.id,
        name: connection.name,
        source: connection.source,
        target: connection.target,
        data: edgeData,
      });
    }

    const starterNodeComponent = this.nodes.find(
      (node) => node.type === "starter",
    )!.component as StarterNodeComponent;

    const phoneNodeComponent = this.nodes.find((node) => node.type === "phone")
      ?.component as PhoneNodeComponent;

    const dto = {
      account_app_id: !starterNodeComponent.entryEventType.internal ? (this.flow?.account_app_id ?? this.SessionService.currentAppValue!.id) : null,
      id: this.flow?.id,
      logic,
      simple: true,
      disabled: false,
    };

    merge(dto, starterNodeComponent.flowDTO, phoneNodeComponent?.flowDTO ?? {});

    if (this.newFlow) {
      this.uiService
        .waitForEvent(
          this.FlowsService.createLogicTree(
            this.SessionService.currentAccountValue!.accountID,
            dto,
          ),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          (logicTree) => {
            this.NotificationService.openSuccessSnackBar("Flow created");
            if(this.internal){
              this.Router.navigate(["flows", "library", "internal"]);
            } else {
              this.Router.navigate(["flows", "library", "app"]);
            }
          },
          () => {
            this.NotificationService.openErrorSnackBar("Error creating flow");
          },
        );
    } else {
      this.uiService
        .waitForEvent(
          this.FlowsService.updateLogicTree(
            this.SessionService.currentAccountValue!.accountID,
            dto.id,
            dto,
          ),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          (logicTree) => {
            this.NotificationService.openSuccessSnackBar("Flow updated");
            if(this.internal){
              this.Router.navigate(["flows", "library", "internal"]);
            } else {
              this.Router.navigate(["flows", "library", "app"]);
            }
            
          },
          () => {
            this.NotificationService.openErrorSnackBar("Error updating flow");
          },
        );
    }
  }

  get everyNodeHasConnection() {
    for (let node of this.nodes) {
      if (
        !this.connections.find(
          (connection) =>
            connection.source === node.id || connection.target === node.id,
        )
      ) {
        return false;
      }
    }
    return true;
  }

  get everyNodeValid() {
    for (let node of this.nodes) {
      if (node.component?.invalid) {
        return false;
      }
    }
    return true;
  }

  get everyConnectionValid() {
    for (let connection of this.connections) {
      if (!connection.component?.conditionsValid) {
        return false;
      }
    }

    return true;
  }

  get flowSaveDisabled() {
    return (
      !this.everyNodeHasConnection ||
      !this.everyNodeValid ||
      !this.everyConnectionValid
    );
  }

  get hasChannelNode() {
    return !!this.nodes.find((node) => node.channelNode);
  }

  get starterNode(): StarterNodeComponent|null {
    if(this.nodes.length == 0){
      return null;
    }
    const starterNodeComponent = this.nodes.find(
      (node) => node.type === "starter",
    )!.component as StarterNodeComponent;
    return starterNodeComponent || null;
  }

  get trigger() {
    if(this.starterNode == null){
      return null;
    }
    return this.starterNode.entryEventType;
    
  }

  hasTrigger() {
    return this.trigger !== null;
  }

}
